import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { SortEvent, ConfirmationService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  TfColumnDefDirective,
  TfDataTableComponent,
  TfPageEvent,
} from '@tourfold/libraries/shared-ui';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  Brand,
  BrandFaviconUploadStatusStatusEnum,
  BrandLogoUploadStatusStatusEnum,
  CreateBrand,
  LinkedCustomDomain,
  PatchBrand,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  CustomDomain,
  CustomDomainsApi,
  CustomDomainStatusEnum,
  FrontendThemeApi,
  FrontendThemeItem,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { SelectModule } from 'primeng/select';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BrandsService } from './brands.service';
import { provideBrandsAiPageContext } from './brands-list.ai-page-context';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { ServerFieldErrors } from '@tourfold/libraries/shared-util';
import { ThemePreviewComponent } from '@tourfold/features/settings/theme';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

@Component({
  selector: 'tf-settings-brands-list',
  imports: [
    CommonModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    ConfirmDialogModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    TfDataTableComponent,
    TfColumnDefDirective,
    SelectModule,
    RouterLink,
    ThemePreviewComponent,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './brands-list.html',
  styleUrl: './brands-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class Brands {
  private static readonly LOGO_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
  private static readonly ALLOWED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg'];
  private static readonly LOGO_UPLOAD_POLL_INTERVAL_MS = 1000;
  private static readonly LOGO_UPLOAD_MAX_POLL_ATTEMPTS = 15;
  private static readonly FAVICON_MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024;
  private static readonly ALLOWED_FAVICON_MIME_TYPES = ['image/png'];
  private static readonly FAVICON_UPLOAD_POLL_INTERVAL_MS = 1000;
  private static readonly FAVICON_UPLOAD_MAX_POLL_ATTEMPTS = 15;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly brandsService = inject(BrandsService);
  private readonly customDomainsApi = inject(CustomDomainsApi);
  private readonly frontendThemeApi = inject(FrontendThemeApi);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly canCreateBrand = computed(() =>
    this.resourceAccessService.canPerform('brands', 'create')
  );
  readonly canUpdateBrand = computed(() =>
    this.resourceAccessService.canPerform('brands', 'update')
  );
  readonly canDeleteBrand = computed(() =>
    this.resourceAccessService.canPerform('brands', 'delete')
  );
  /**
   * `resource_access.customDomains` is `enabled + includes 'read'` only when the
   * tenant has the custom-domains feature flag on AND the actor has
   * `customDomains:read`. Without that we hide the linked-domains column entirely
   * because the rest of the surface (manage link, dialog list) is gated the same way.
   */
  readonly canReadCustomDomains = computed(() =>
    this.resourceAccessService.canPerform('customDomains', 'read')
  );
  readonly canReadThemes = computed(() => this.resourceAccessService.canPerform('theme', 'read'));
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchSubject = new Subject<string>();
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly fb = inject(FormBuilder);

  readonly brands = signal<Brand[]>([]);
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  /**
   * Errors raised while a create/edit dialog is open. Rendered *inside* the
   * dialog instead of `actionError`, which renders in the page body behind the
   * modal overlay and would otherwise be invisible (TF-2069). Used for
   * save/link failures that aren't tied to a specific field. Logo/favicon
   * upload validation errors get their own signals so they render right under
   * the box that produced them (see [logoError] / [faviconError]).
   */
  readonly dialogError = signal<string | null>(null);
  /** Logo file validation error, shown under the logo card in the open dialog. */
  readonly logoError = signal<string | null>(null);
  /** Favicon file validation error, shown under the favicon card in the open dialog. */
  readonly faviconError = signal<string | null>(null);
  readonly isUploadingLogo = signal(false);
  readonly logoUploadMessage = signal<string | null>(null);
  readonly isEditDialogVisible = signal<boolean>(false);
  readonly isCreateDialogVisible = signal<boolean>(false);
  readonly searchQuery = signal<string>('');
  readonly totalRecords = signal(0);
  readonly originalBrand = signal<Brand | null>(null);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal<number>(0);
  readonly showEmptyState = computed(
    () => this.hasLoadedOnce() && this.searchQuery().trim() === '' && this.totalRecords() === 0
  );

  readonly createLogoFile = signal<File | null>(null);
  readonly createLogoPreviewUrl = signal<string | null>(null);

  readonly editLogoFile = signal<File | null>(null);
  readonly editLogoPreviewUrl = signal<string | null>(null);
  readonly removeExistingLogo = signal(false);
  /**
   * Preview shown in the edit-dialog logo card:
   * - staged file data URL if any
   * - else null when the user marked the current logo for removal
   * - else the brand's current logo URL
   */
  readonly editLogoCardPreviewUrl = computed<string | null>(() => {
    const staged = this.editLogoPreviewUrl();
    if (staged) {
      return staged;
    }
    if (this.removeExistingLogo()) {
      return null;
    }
    return this.originalBrand()?.logoUrl ?? null;
  });

  readonly isUploadingFavicon = signal(false);
  readonly faviconUploadMessage = signal<string | null>(null);

  readonly createFaviconFile = signal<File | null>(null);
  /** Data URL of the staged favicon (create flow). Set after FileReader resolves. */
  readonly createFaviconStagedPreviewUrl = signal<string | null>(null);
  /** Preview shown in the create card: staged data URL if any, else null. */
  readonly createFaviconPreviewUrl = computed(() => this.createFaviconStagedPreviewUrl());

  readonly editFaviconFile = signal<File | null>(null);
  /** Data URL of the staged favicon (edit flow). Set after FileReader resolves. */
  readonly editFaviconStagedPreviewUrl = signal<string | null>(null);
  readonly removeExistingFavicon = signal(false);
  /**
   * Preview shown in the edit card:
   * - staged file data URL if any
   * - else null when the user marked the current favicon for removal
   * - else the brand's current favicon URL
   */
  readonly editFaviconPreviewUrl = computed<string | null>(() => {
    const staged = this.editFaviconStagedPreviewUrl();
    if (staged) {
      return staged;
    }
    if (this.removeExistingFavicon()) {
      return null;
    }
    return this.originalBrand()?.faviconUrl ?? null;
  });

  /**
   * The brand's currently linked domains. Edits flow through `linkDomain` /
   * `unlinkDomain` which call the API and mutate this signal optimistically —
   * not through the form. The signal is initialised from the brand when the
   * edit dialog opens and cleared when it closes.
   */
  readonly editLinkedDomains = signal<LinkedCustomDomain[]>([]);
  /**
   * Staged links for the Create flow: the brand doesn't exist yet, so we just
   * collect the chosen verified domains and POST the link calls after the brand
   * itself has been created. Cleared when the dialog closes.
   */
  readonly createStagedDomains = signal<CustomDomain[]>([]);
  /** Every verified custom domain in the tenant, loaded when either dialog opens. */
  readonly verifiedCustomDomains = signal<CustomDomain[]>([]);
  /** Built-in default plus custom themes, loaded when either dialog opens and themes are enabled. */
  readonly availableThemes = signal<FrontendThemeItem[]>([]);
  readonly customThemes = computed(() => this.availableThemes().filter((theme) => !theme.builtIn));
  readonly activeWorkspaceTheme = computed(
    () =>
      this.availableThemes().find((theme) => theme.active) ??
      this.availableThemes().find((theme) => theme.builtIn) ??
      null
  );
  /** Verified domains that aren't already linked to the brand being edited. */
  readonly availableCustomDomains = computed<CustomDomain[]>(() => {
    const linkedIds = new Set(this.editLinkedDomains().map((d) => d.id));
    return this.verifiedCustomDomains().filter((d) => !linkedIds.has(d.id));
  });
  /** Verified domains not yet staged on the in-flight Create form. */
  readonly availableCustomDomainsForCreate = computed<CustomDomain[]>(() => {
    const stagedIds = new Set(this.createStagedDomains().map((d) => d.id));
    return this.verifiedCustomDomains().filter((d) => !stagedIds.has(d.id));
  });
  /** Section visibility for the Edit dialog: hidden when there's nothing to show or link. */
  readonly editLinkedDomainsSectionVisible = computed(
    () => this.editLinkedDomains().length > 0 || this.availableCustomDomains().length > 0
  );
  /** Section visibility for the Create dialog: hidden when no verified domains exist at all. */
  readonly createLinkedDomainsSectionVisible = computed(
    () => this.verifiedCustomDomains().length > 0
  );
  readonly isPickingDomain = signal(false);
  readonly isPickingDomainForCreate = signal(false);
  readonly isMutatingLink = signal<string | null>(null);

  readonly editBrandForm = this.fb.group({
    name: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.email]),
    phoneNumber: new FormControl<string>(''),
    themeId: new FormControl<string | null>(null),
  });

  readonly createBrandForm = this.fb.group({
    name: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.email]),
    phoneNumber: new FormControl<string>(''),
    themeId: new FormControl<string | null>(null),
  });

  // Server-side validation errors (populated from RFC 9457 ProblemDetail responses).
  // Map `phone_number` (potential snake_case pointer from backend) to the `phoneNumber` control.
  readonly createServerErrors = new ServerFieldErrors(this.createBrandForm, {
    pointerToField: { phone_number: 'phoneNumber', theme_id: 'themeId' },
  });
  readonly editServerErrors = new ServerFieldErrors(this.editBrandForm, {
    pointerToField: { phone_number: 'phoneNumber', theme_id: 'themeId' },
  });

  readonly columns = computed<ColumnDef[]>(() => {
    const cols: ColumnDef[] = [
      { field: 'name', title: 'SETTINGS_BRANDS.NAME', type: 'text', sortable: true, width: '20%' },
      {
        field: 'email',
        title: 'SETTINGS_BRANDS.EMAIL',
        type: 'text',
        sortable: true,
        width: '20%',
      },
      {
        field: 'phoneNumber',
        title: 'SETTINGS_BRANDS.PHONE_NUMBER',
        type: 'text',
        sortable: true,
        width: '15%',
      },
      { field: 'logoUrl', title: 'SETTINGS_BRANDS.LOGO', type: 'custom', width: '10%' },
    ];
    if (this.canReadThemes()) {
      cols.push({
        field: 'linkedTheme',
        title: 'SETTINGS_BRANDS.THEME',
        type: 'custom',
        width: '18%',
      });
    }
    if (this.canReadCustomDomains()) {
      cols.push({
        field: 'linkedCustomDomains',
        title: 'SETTINGS_BRANDS.LINKED_CUSTOM_DOMAINS',
        type: 'custom',
        width: '25%',
      });
    }
    cols.push({ field: '_actions', title: 'SETTINGS_BRANDS.ACTIONS', type: 'custom' });
    return cols;
  });

  /** Hard cap on the number of linked-domain chips shown inline in the list cell. */
  static readonly MAX_INLINE_LINKED_DOMAIN_CHIPS = 2;

  /** Visible chips from a brand's linked domains, capped at [MAX_INLINE_LINKED_DOMAIN_CHIPS]. */
  visibleLinkedDomains(linked: LinkedCustomDomain[] | undefined | null): LinkedCustomDomain[] {
    return (linked ?? []).slice(0, Brands.MAX_INLINE_LINKED_DOMAIN_CHIPS);
  }

  /** How many additional linked domains are hidden behind "+N more". 0 means none hidden. */
  remainingLinkedDomainsCount(linked: LinkedCustomDomain[] | undefined | null): number {
    const total = linked?.length ?? 0;
    return Math.max(0, total - Brands.MAX_INLINE_LINKED_DOMAIN_CHIPS);
  }

  selectedEditTheme(): FrontendThemeItem | null {
    return this.findThemeById(this.editBrandForm.controls.themeId.value);
  }

  selectedCreateTheme(): FrontendThemeItem | null {
    return this.findThemeById(this.createBrandForm.controls.themeId.value);
  }

  linkedThemeForBrand(brand: Brand): FrontendThemeItem | null {
    return this.findThemeById(brand.themeId);
  }

  linkedThemeName(brand: Brand): string {
    return this.linkedThemeForBrand(brand)?.name ?? brand.linkedTheme?.name ?? brand.themeId ?? '';
  }

  useWorkspaceTheme(target: 'create' | 'edit'): void {
    const control =
      target === 'create'
        ? this.createBrandForm.controls.themeId
        : this.editBrandForm.controls.themeId;
    control.setValue(null);
    control.markAsDirty();
  }

  useCustomTheme(target: 'create' | 'edit'): void {
    const control =
      target === 'create'
        ? this.createBrandForm.controls.themeId
        : this.editBrandForm.controls.themeId;
    if (!control.value) {
      control.setValue(this.customThemes()[0]?.id ?? null);
    }
    control.markAsDirty();
  }

  readonly editableBrands = computed(() => this.brands());

  constructor() {
    this.setupSearchDebounce();
    this.loadBrands();
    void this.loadThemesIfAvailable();
    provideBrandsAiPageContext({
      brands: this.brands,
      searchQuery: this.searchQuery,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      originalBrand: this.originalBrand,
      isEditDialogVisible: this.isEditDialogVisible,
    });
  }

  private setupSearchDebounce() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.searchQuery.set(query);
        this.loadBrands(0, 50);
      });
  }

  private loadBrands(page = 0, size = 50) {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);

    const sortArray: string[] = [];
    const field = this.sortField();
    const order = this.sortOrder();
    if (field && order !== 0) {
      const backendField = this.mapFieldToBackend(field);
      const direction = order === 1 ? 'asc' : 'desc';
      sortArray.push(`${backendField}:${direction}`);
    }

    this.brandsService
      .getBrandsData({
        page,
        size,
        sort: sortArray.length > 0 ? sortArray : undefined,
        search: this.searchQuery() ?? undefined,
      })
      .subscribe({
        next: (response) => {
          this.brands.set(response.items);
          this.totalRecords.set(response.page.totalElements);
          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const message = this.translateService.instant('SETTINGS.LOAD_ERROR.TITLE');
          if (hadLoadedOnce) {
            this.hasLoadedOnce.set(true);
            this.actionError.set(message);
          } else {
            this.loadError.set(message);
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          }
        },
      });
  }

  private mapFieldToBackend(field: string): string {
    const fieldMap: Record<string, string> = {
      name: 'name',
      email: 'email',
      phoneNumber: 'phone_number',
      createdAt: 'created_at',
    };
    return fieldMap[field] ?? field;
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 0;

    if (this.sortField() === newField && this.sortOrder() === newOrder) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);

    this.loadBrands(0, 50);
  }

  onPageChange(event: TfPageEvent): void {
    const page = Math.floor(event.first / event.rows);
    this.loadBrands(page, event.rows);
  }

  reloadBrands(): void {
    this.loadBrands(0, 50);
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  onCreateLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      input.value = '';
      return;
    }

    const validationError = this.validateLogoFile(file);
    if (validationError) {
      input.value = '';
      this.logoError.set(validationError);
      return;
    }

    // Store file reference and start preview before resetting input.
    // Safari invalidates file data when input.value is cleared; reset happens inside the FileReader callback.
    this.clearCreateLogoSelection();
    this.createLogoFile.set(file);
    this.loadLogoPreview(file, 'create', input);
  }

  clearCreateLogoSelection(input?: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }
    this.createLogoFile.set(null);
    this.createLogoPreviewUrl.set(null);
    this.logoError.set(null);
  }

  onEditLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      input.value = '';
      return;
    }

    const validationError = this.validateLogoFile(file);
    if (validationError) {
      input.value = '';
      this.logoError.set(validationError);
      return;
    }

    // Store file reference and start preview before resetting input.
    // Safari invalidates file data when input.value is cleared; reset happens inside the FileReader callback.
    this.clearEditLogoSelection();
    this.removeExistingLogo.set(false);
    this.editLogoFile.set(file);
    this.loadLogoPreview(file, 'edit', input);
  }

  clearEditLogoSelection(input?: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }
    this.editLogoFile.set(null);
    this.editLogoPreviewUrl.set(null);
    this.logoError.set(null);
  }

  private loadLogoPreview(file: File, target: 'create' | 'edit', input: HTMLInputElement): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      // Reset input only after reading completes — resetting earlier invalidates file data in Safari.
      input.value = '';
      const dataUrl = e.target?.result as string;
      if (target === 'create') {
        this.createLogoPreviewUrl.set(dataUrl);
      } else {
        this.editLogoPreviewUrl.set(dataUrl);
      }
      // FileReader callbacks run outside Angular's zone in some browsers, so signal writes
      // alone may not schedule a CD cycle for OnPush components. markForCheck() ensures the
      // view is checked on the next cycle — harmless once the app switches to zoneless.
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  markLogoForRemoval(): void {
    this.clearEditLogoSelection();
    this.removeExistingLogo.set(true);
  }

  undoLogoRemoval(): void {
    this.removeExistingLogo.set(false);
  }

  onCreateFaviconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      input.value = '';
      return;
    }

    const validationError = this.validateFaviconFile(file);
    if (validationError) {
      input.value = '';
      this.faviconError.set(validationError);
      return;
    }

    this.clearCreateFaviconSelection();
    this.createFaviconFile.set(file);
    this.loadFaviconPreview(file, 'create', input);
  }

  clearCreateFaviconSelection(input?: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }
    this.createFaviconFile.set(null);
    this.createFaviconStagedPreviewUrl.set(null);
    this.faviconError.set(null);
  }

  onEditFaviconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      input.value = '';
      return;
    }

    const validationError = this.validateFaviconFile(file);
    if (validationError) {
      input.value = '';
      this.faviconError.set(validationError);
      return;
    }

    this.clearEditFaviconSelection();
    this.removeExistingFavicon.set(false);
    this.editFaviconFile.set(file);
    this.loadFaviconPreview(file, 'edit', input);
  }

  clearEditFaviconSelection(input?: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }
    this.editFaviconFile.set(null);
    this.editFaviconStagedPreviewUrl.set(null);
    this.faviconError.set(null);
  }

  private loadFaviconPreview(file: File, target: 'create' | 'edit', input: HTMLInputElement): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      input.value = '';
      const dataUrl = e.target?.result as string;
      if (target === 'create') {
        this.createFaviconStagedPreviewUrl.set(dataUrl);
      } else {
        this.editFaviconStagedPreviewUrl.set(dataUrl);
      }
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  markFaviconForRemoval(): void {
    this.clearEditFaviconSelection();
    this.removeExistingFavicon.set(true);
  }

  undoFaviconRemoval(): void {
    this.removeExistingFavicon.set(false);
  }

  editBrand(brand: Brand): void {
    this.originalBrand.set(brand);
    this.dialogError.set(null);
    this.logoError.set(null);
    this.faviconError.set(null);
    this.logoUploadMessage.set(null);
    this.faviconUploadMessage.set(null);
    this.removeExistingLogo.set(false);
    this.removeExistingFavicon.set(false);
    this.editServerErrors.clear();
    this.editBrandForm.patchValue({
      name: brand.name ?? '',
      email: brand.email ?? '',
      phoneNumber: brand.phoneNumber ?? '',
      themeId: brand.themeId ?? null,
    });
    this.editLinkedDomains.set([...(brand.linkedCustomDomains ?? [])]);
    this.isPickingDomain.set(false);
    this.isMutatingLink.set(null);
    void this.loadVerifiedCustomDomains();
    void this.loadThemesIfAvailable();

    setTimeout(() => {
      this.isEditDialogVisible.set(true);
    }, 0);
  }

  private async loadVerifiedCustomDomains(): Promise<void> {
    try {
      const response = await this.customDomainsApi.listCustomDomains();
      this.verifiedCustomDomains.set(
        response.items.filter((d) => d.status === CustomDomainStatusEnum.Verified)
      );
    } catch {
      this.verifiedCustomDomains.set([]);
    }
  }

  private async loadThemesIfAvailable(): Promise<void> {
    if (!this.canReadThemes()) {
      this.availableThemes.set([]);
      return;
    }
    try {
      const response = await this.frontendThemeApi.listFrontendThemes();
      this.availableThemes.set(response.items);
    } catch {
      this.availableThemes.set([]);
    }
  }

  async linkDomain(domain: CustomDomain): Promise<void> {
    const brand = this.originalBrand();
    if (!brand || this.isMutatingLink() !== null) return;
    this.isMutatingLink.set(domain.id);
    try {
      await firstValueFrom(this.brandsService.linkCustomDomain(brand.id, domain.id));
      this.editLinkedDomains.update((current) => [
        ...current,
        { id: domain.id, domain: domain.domain },
      ]);
      this.isPickingDomain.set(false);
    } catch (err: unknown) {
      this.dialogError.set(this.extractErrorMessage(err, 'SETTINGS_BRANDS.ERROR.LINK_FAILED'));
    } finally {
      this.isMutatingLink.set(null);
    }
  }

  async unlinkDomain(link: LinkedCustomDomain): Promise<void> {
    const brand = this.originalBrand();
    if (!brand || this.isMutatingLink() !== null) return;
    this.isMutatingLink.set(link.id);
    try {
      await firstValueFrom(this.brandsService.unlinkCustomDomain(brand.id, link.id));
      this.editLinkedDomains.update((current) => current.filter((d) => d.id !== link.id));
    } catch (err: unknown) {
      this.dialogError.set(this.extractErrorMessage(err, 'SETTINGS_BRANDS.ERROR.UNLINK_FAILED'));
    } finally {
      this.isMutatingLink.set(null);
    }
  }

  toggleDomainPicker(): void {
    this.isPickingDomain.update((open) => !open);
  }

  private extractErrorMessage(err: unknown, fallbackKey: string): string {
    if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
      return err.message;
    }
    return this.translateService.instant(fallbackKey);
  }

  async saveEditing(): Promise<void> {
    if (this.editBrandForm.invalid) {
      this.editBrandForm.markAllAsTouched();
      return;
    }

    const original = this.originalBrand();
    if (!original) {
      return;
    }

    const hasFormChanges = !this.editBrandForm.pristine;
    const logoFile = this.editLogoFile();
    const hasPendingLogoRemoval = this.removeExistingLogo();
    const faviconFile = this.editFaviconFile();
    const hasPendingFaviconRemoval = this.removeExistingFavicon();
    if (
      !hasFormChanges &&
      !logoFile &&
      !hasPendingLogoRemoval &&
      !faviconFile &&
      !hasPendingFaviconRemoval
    ) {
      return;
    }

    this.editServerErrors.clear();
    this.dialogError.set(null);
    this.isLoading.set(true);
    this.logoUploadMessage.set(null);
    this.faviconUploadMessage.set(null);

    try {
      if (hasFormChanges) {
        const formValue = this.editBrandForm.getRawValue();
        const patchBrand: PatchBrand = {
          lockVersion: original.lockVersion,
          name: formValue.name?.trim() ?? '',
          email: formValue.email?.trim() ?? undefined,
          phoneNumber: formValue.phoneNumber?.trim() ?? undefined,
          themeId: this.themeIdForPatch(formValue.themeId),
        };

        await firstValueFrom(this.brandsService.patchBrand(original.id, patchBrand));
      }

      await this.applyPendingLogoChange(original.id, logoFile, hasPendingLogoRemoval);
      await this.applyPendingFaviconChange(original.id, faviconFile, hasPendingFaviconRemoval);

      this.closeEditDialog();
      this.loadBrands(0, 50);
    } catch (error: unknown) {
      const applied = await this.editServerErrors.apply(error);
      if (applied && applied.fieldCount > 0) {
        if (applied.generalMessages.length > 0) {
          this.dialogError.set(applied.generalMessages.join(' '));
        }
        return;
      }
      const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
      const fallback =
        joinedGeneral !== ''
          ? joinedGeneral
          : this.translateService.instant('SETTINGS_BRANDS.ERROR.BRAND_UPDATE_FAILED');
      this.dialogError.set(fallback);
    } finally {
      this.isLoading.set(false);
      this.isUploadingLogo.set(false);
      this.isUploadingFavicon.set(false);
      this.logoUploadMessage.set(null);
      this.faviconUploadMessage.set(null);
    }
  }

  closeEditDialog(): void {
    this.isEditDialogVisible.set(false);
    this.editBrandForm.reset();
    this.editServerErrors.clear();
    this.dialogError.set(null);
    this.originalBrand.set(null);
    this.clearEditLogoSelection();
    this.clearEditFaviconSelection();
    this.removeExistingLogo.set(false);
    this.removeExistingFavicon.set(false);
    this.logoUploadMessage.set(null);
    this.faviconUploadMessage.set(null);
    this.editLinkedDomains.set([]);
    this.verifiedCustomDomains.set([]);
    this.isPickingDomain.set(false);
    this.isMutatingLink.set(null);
  }

  onEditDialogVisibleChange(visible: boolean): void {
    if (visible) {
      this.isEditDialogVisible.set(true);
      return;
    }

    this.closeEditDialog();
  }

  createBrand(): void {
    this.createBrandForm.reset({
      name: '',
      email: '',
      phoneNumber: '',
      themeId: null,
    });
    this.createServerErrors.clear();
    this.dialogError.set(null);
    this.logoError.set(null);
    this.faviconError.set(null);
    this.logoUploadMessage.set(null);
    this.faviconUploadMessage.set(null);
    this.createStagedDomains.set([]);
    this.isPickingDomainForCreate.set(false);
    void this.loadVerifiedCustomDomains();
    void this.loadThemesIfAvailable();
    this.isCreateDialogVisible.set(true);
  }

  stageDomainForCreate(domain: CustomDomain): void {
    this.createStagedDomains.update((current) => [...current, domain]);
    this.isPickingDomainForCreate.set(false);
  }

  unstageDomainForCreate(domainId: string): void {
    this.createStagedDomains.update((current) => current.filter((d) => d.id !== domainId));
  }

  toggleDomainPickerForCreate(): void {
    this.isPickingDomainForCreate.update((open) => !open);
  }

  async saveNewBrand(): Promise<void> {
    if (this.createBrandForm.invalid) {
      this.createBrandForm.markAllAsTouched();
      return;
    }

    const formValue = this.createBrandForm.getRawValue();
    const createBrand: CreateBrand = {
      name: formValue.name?.trim() ?? '',
      email: formValue.email?.trim() ?? undefined,
      phoneNumber: formValue.phoneNumber?.trim() ?? undefined,
      themeId: formValue.themeId ?? undefined,
    };

    this.createServerErrors.clear();
    this.dialogError.set(null);
    this.isLoading.set(true);
    this.logoUploadMessage.set(null);
    this.faviconUploadMessage.set(null);

    try {
      const createdBrand = await firstValueFrom(this.brandsService.createBrand(createBrand));
      const logoFile = this.createLogoFile();
      if (logoFile) {
        await this.uploadBrandLogoAndWaitUntilApplied(createdBrand.id, logoFile);
      }
      const faviconFile = this.createFaviconFile();
      if (faviconFile) {
        await this.uploadBrandFaviconAndWaitUntilApplied(createdBrand.id, faviconFile);
      }

      const staged = this.createStagedDomains();
      for (const domain of staged) {
        try {
          await firstValueFrom(this.brandsService.linkCustomDomain(createdBrand.id, domain.id));
        } catch {
          // Don't block brand creation: the brand exists, just one link failed.
          // The dialog closes right after (creation succeeded), so surface this on
          // the table via `actionError` — it must outlive the dialog, not `dialogError`.
          this.actionError.set(
            this.translateService.instant('SETTINGS_BRANDS.ERROR.LINK_FAILED') + ' ' + domain.domain
          );
        }
      }

      this.closeCreateDialog();
      this.loadBrands(0, 50);
    } catch (error: unknown) {
      const applied = await this.createServerErrors.apply(error);
      if (applied && applied.fieldCount > 0) {
        if (applied.generalMessages.length > 0) {
          this.dialogError.set(applied.generalMessages.join(' '));
        }
        return;
      }
      const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
      const fallback =
        joinedGeneral !== ''
          ? joinedGeneral
          : this.translateService.instant('SETTINGS_BRANDS.ERROR.BRAND_CREATE_FAILED');
      this.dialogError.set(fallback);
    } finally {
      this.isLoading.set(false);
      this.isUploadingLogo.set(false);
      this.isUploadingFavicon.set(false);
      this.logoUploadMessage.set(null);
      this.faviconUploadMessage.set(null);
    }
  }

  closeCreateDialog(): void {
    this.isCreateDialogVisible.set(false);
    this.createBrandForm.reset({
      name: '',
      email: '',
      phoneNumber: '',
      themeId: null,
    });
    this.createServerErrors.clear();
    this.dialogError.set(null);
    this.clearCreateLogoSelection();
    this.clearCreateFaviconSelection();
    this.logoUploadMessage.set(null);
    this.faviconUploadMessage.set(null);
    this.createStagedDomains.set([]);
    this.isPickingDomainForCreate.set(false);
    this.verifiedCustomDomains.set([]);
  }

  onCreateDialogVisibleChange(visible: boolean): void {
    if (visible) {
      this.isCreateDialogVisible.set(true);
      return;
    }

    this.closeCreateDialog();
  }

  deleteBrand(brand: Brand): void {
    const translatedMessage = this.translateService.instant(
      'SETTINGS_BRANDS.DELETE_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', brand.name);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_BRANDS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.brandsService.deleteBrand(brand.id).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.loadBrands(0, 50);
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            let errorMessage = this.translateService.instant(
              'SETTINGS_BRANDS.ERROR.BRAND_DELETE_FAILED'
            );
            if (error && typeof error === 'object' && 'error' in error) {
              const httpError = error as { error?: unknown };
              if (httpError.error && typeof httpError.error === 'object') {
                const errorBody = httpError.error as Record<string, unknown>;
                if (typeof errorBody.message === 'string' && errorBody.message.length > 0) {
                  errorMessage = errorBody.message;
                }
              }
            }
            this.actionError.set(errorMessage);
          },
        });
      },
    });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  clearDialogError(): void {
    this.dialogError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }

  private validateLogoFile(file: File): string | null {
    if (!Brands.ALLOWED_LOGO_MIME_TYPES.includes(file.type)) {
      return this.translateService.instant('SETTINGS_BRANDS.ERROR.LOGO_INVALID_FILE_TYPE');
    }

    if (file.size > Brands.LOGO_MAX_FILE_SIZE_BYTES) {
      return this.translateService.instant('SETTINGS_BRANDS.ERROR.LOGO_FILE_TOO_LARGE');
    }

    return null;
  }

  private validateFaviconFile(file: File): string | null {
    if (!Brands.ALLOWED_FAVICON_MIME_TYPES.includes(file.type)) {
      return this.translateService.instant('SETTINGS_BRANDS.ERROR.FAVICON_INVALID_FILE_TYPE');
    }

    if (file.size > Brands.FAVICON_MAX_FILE_SIZE_BYTES) {
      return this.translateService.instant('SETTINGS_BRANDS.ERROR.FAVICON_FILE_TOO_LARGE');
    }

    return null;
  }

  private async applyPendingLogoChange(
    brandId: string,
    logoFile: File | null,
    hasPendingLogoRemoval: boolean
  ): Promise<void> {
    if (hasPendingLogoRemoval) {
      await firstValueFrom(this.brandsService.deleteBrandLogo(brandId));
    }
    if (logoFile) {
      await this.uploadBrandLogoAndWaitUntilApplied(brandId, logoFile);
    }
  }

  private async applyPendingFaviconChange(
    brandId: string,
    faviconFile: File | null,
    hasPendingFaviconRemoval: boolean
  ): Promise<void> {
    if (hasPendingFaviconRemoval) {
      await firstValueFrom(this.brandsService.deleteBrandFavicon(brandId));
    }
    if (faviconFile) {
      await this.uploadBrandFaviconAndWaitUntilApplied(brandId, faviconFile);
    }
  }

  private async uploadBrandLogoAndWaitUntilApplied(brandId: string, file: File): Promise<void> {
    this.isUploadingLogo.set(true);
    this.logoUploadMessage.set(
      this.translateService.instant('SETTINGS_BRANDS.LOGO_STATUS.REQUESTING')
    );

    const uploadIntent = await firstValueFrom(
      this.brandsService.createLogoUploadIntent(brandId, file.name, file.type)
    );

    this.logoUploadMessage.set(
      this.translateService.instant('SETTINGS_BRANDS.LOGO_STATUS.UPLOADING')
    );
    await firstValueFrom(
      this.brandsService.uploadLogoToPresignedPost(
        uploadIntent.uploadUrl,
        uploadIntent.uploadFields,
        file
      )
    );

    this.logoUploadMessage.set(
      this.translateService.instant('SETTINGS_BRANDS.LOGO_STATUS.PROCESSING')
    );

    for (let attempt = 0; attempt < Brands.LOGO_UPLOAD_MAX_POLL_ATTEMPTS; attempt++) {
      const status = await firstValueFrom(
        this.brandsService.getLogoUploadStatus(brandId, uploadIntent.fileId)
      );

      if (
        status.status === BrandLogoUploadStatusStatusEnum.Failed ||
        status.status === BrandLogoUploadStatusStatusEnum.UploadRejected
      ) {
        throw new Error(
          this.translateService.instant('SETTINGS_BRANDS.ERROR.LOGO_UPLOAD_REJECTED')
        );
      }

      if (status.logoApplied) {
        this.logoUploadMessage.set(
          this.translateService.instant('SETTINGS_BRANDS.LOGO_STATUS.DONE')
        );
        return;
      }

      await this.delay(Brands.LOGO_UPLOAD_POLL_INTERVAL_MS);
    }

    throw new Error(this.translateService.instant('SETTINGS_BRANDS.ERROR.LOGO_UPLOAD_TIMEOUT'));
  }

  private async uploadBrandFaviconAndWaitUntilApplied(brandId: string, file: File): Promise<void> {
    this.isUploadingFavicon.set(true);
    this.faviconUploadMessage.set(
      this.translateService.instant('SETTINGS_BRANDS.FAVICON_STATUS.REQUESTING')
    );

    const uploadIntent = await firstValueFrom(
      this.brandsService.createFaviconUploadIntent(brandId, file.name, file.type)
    );

    this.faviconUploadMessage.set(
      this.translateService.instant('SETTINGS_BRANDS.FAVICON_STATUS.UPLOADING')
    );
    await firstValueFrom(
      this.brandsService.uploadFaviconToPresignedPost(
        uploadIntent.uploadUrl,
        uploadIntent.uploadFields,
        file
      )
    );

    this.faviconUploadMessage.set(
      this.translateService.instant('SETTINGS_BRANDS.FAVICON_STATUS.PROCESSING')
    );

    for (let attempt = 0; attempt < Brands.FAVICON_UPLOAD_MAX_POLL_ATTEMPTS; attempt++) {
      const status = await firstValueFrom(
        this.brandsService.getFaviconUploadStatus(brandId, uploadIntent.fileId)
      );

      if (
        status.status === BrandFaviconUploadStatusStatusEnum.Failed ||
        status.status === BrandFaviconUploadStatusStatusEnum.UploadRejected
      ) {
        throw new Error(
          this.translateService.instant('SETTINGS_BRANDS.ERROR.FAVICON_UPLOAD_REJECTED')
        );
      }

      if (status.faviconApplied) {
        this.faviconUploadMessage.set(
          this.translateService.instant('SETTINGS_BRANDS.FAVICON_STATUS.DONE')
        );
        return;
      }

      await this.delay(Brands.FAVICON_UPLOAD_POLL_INTERVAL_MS);
    }

    throw new Error(this.translateService.instant('SETTINGS_BRANDS.ERROR.FAVICON_UPLOAD_TIMEOUT'));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  private findThemeById(themeId: string | null | undefined): FrontendThemeItem | null {
    if (!themeId) {
      return null;
    }
    return this.availableThemes().find((theme) => theme.id === themeId) ?? null;
  }

  private themeIdForPatch(themeId: string | null | undefined): string | undefined {
    return themeId ?? (null as unknown as string);
  }
}
